/**
 * Shared fetch wrapper. Per ADR-039:
 *
 *   - Always sends credentials so the `bluforce_sid` HttpOnly cookie
 *     rides on every same-origin request.
 *   - On unsafe methods (POST/PUT/PATCH/DELETE), reads the
 *     `bluforce_csrf` cookie (deliberately not HttpOnly) and echoes it
 *     in the `X-CSRF-Token` header. The API rejects the request if
 *     they don't match.
 *   - On 401 from `/api/*`, redirects to Cognito's hosted login —
 *     pages don't need their own 401 handling.
 *   - On 409 PRODUCER_REQUIRED from `/api/*`, redirects to the
 *     producer picker at `/auth/select-producer`.
 */

import { getLoginUrl } from '../auth/constants.js';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const PRODUCER_PICKER_PATH = '/auth/select-producer';
const ROLE_PICKER_PATH = '/auth/select-role';

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const part of document.cookie.split('; ')) {
    if (part.startsWith(prefix)) return part.slice(prefix.length);
  }
  return undefined;
}

function isUnsafe(method: string | undefined): boolean {
  return !SAFE_METHODS.has((method ?? 'GET').toUpperCase());
}

export interface ApiFetchOptions extends RequestInit {
  /**
   * Skip the automatic redirect to Cognito login on 401. Used by
   * logout (where 401 means "already logged out, no need to bounce")
   * and by tests.
   */
  skipAuthRedirect?: boolean;
}

export async function apiFetch(
  input: string,
  init?: ApiFetchOptions,
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (isUnsafe(init?.method)) {
    const csrf = readCookie('bluforce_csrf');
    if (csrf && !headers.has('X-CSRF-Token')) {
      headers.set('X-CSRF-Token', csrf);
    }
  }

  const skipAuthRedirect = init?.skipAuthRedirect === true;
  const fetchInit: RequestInit = { ...init, credentials: 'include', headers };
  // RequestInit doesn't have `skipAuthRedirect`, so strip it before fetch.
  delete (fetchInit as Record<string, unknown>)['skipAuthRedirect'];
  const res = await fetch(input, fetchInit);

  if (!input.startsWith('/api/')) return res;

  if (res.status === 401 && !skipAuthRedirect) {
    const url = getLoginUrl();
    if (url) window.location.href = url;
    return res;
  }
  if (res.status === 409) {
    // Only redirect on specific contract codes. Other 409s (resource
    // conflicts, etc.) flow back to the caller.
    try {
      const cloned = res.clone();
      const body = (await cloned.json()) as { code?: string };
      if (body?.code === 'PRODUCER_REQUIRED') {
        if (window.location.pathname !== PRODUCER_PICKER_PATH) {
          window.location.href = PRODUCER_PICKER_PATH;
        }
      }
      // BLUF-1323 — same shape for the role picker.
      if (body?.code === 'ROLE_REQUIRED') {
        if (window.location.pathname !== ROLE_PICKER_PATH) {
          window.location.href = ROLE_PICKER_PATH;
        }
      }
    } catch {
      // not JSON — pass through
    }
  }
  return res;
}
